'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, useContext, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { capitalize } from '@/shared/lib/capitalize';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Currency } from '@/shared/model/Currency';

import { Form } from '@/shared/ui/form';
import { Layout } from '@/shared/ui/layout';
import './product-detail.scss';

import { useCartStore, getPriceValueTranslation } from '@/entities/cart';
import { useStorefrontCartMenuData } from '@/entities/product/lib/useStorefrontMenu';
import { ProductDetailContext } from './provider';
import { ProductVariantT } from './types';

type Props = ComponentProps<typeof Layout>;

export const ProductDetailInfoVariantSelect = ({ media, ...props }: Props) => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const getItemPrices = useCartStore((s) => s.getItemPrices);
    const { hash, data, formMethods, switchVariant } = useContext(ProductDetailContext);
    const cartMenuData = useStorefrontCartMenuData();

    const variants = useMemo(() => {
        if (!data) return [];
        const prices = getItemPrices(hash);
        if (!prices) return [];

        const variantPlacements = cartMenuData?.items[hash]?.item?.variants ?? [];
        const defaultVariant = variantPlacements.find((v) => v.isDefault === true);
        const defaultVariantName = defaultVariant
            ? getLocalizedValue(cartMenuData?.items[defaultVariant.hash]?.item?.name ?? data.name)
            : '';

        return [
            {
                id: hash,
                variantId: data.id,
                name: defaultVariantName || capitalize(t('WORDS.DEFAULT')),
                priceValue: {
                    amount: String(prices.itemPrice.amount),
                    currency: String(prices.itemPrice.currency),
                },
            },
            ...variantPlacements
                .filter((v) => v.isDefault !== true && v.public !== false)
                .map((variant) => {
                    const variantHash = variant.hash;
                    const variantItemId = variant.variantItemId;
                    if (!variantItemId) return null;
                    const variantItem = cartMenuData?.items[variantHash]?.item;
                    if (!variantItem) return null;
                    const variantPrices = getItemPrices(variantHash);
                    if (!variantPrices) return null;
                    const localizedName = getLocalizedValue(variantItem.name);
                    if (!localizedName) return null;
                    return {
                        id: variantHash,
                        variantId: variantItemId,
                        name: localizedName,
                        priceValue: {
                            amount: String(variantPrices.itemPrice.amount),
                            currency: String(variantPrices.itemPrice.currency),
                        },
                    };
                }),
        ].filter(Boolean) as ProductVariantT[];
    }, [data, hash, cartMenuData, getItemPrices, getLocalizedValue, t]);

    const selectedVariantId = useWatch({ control: formMethods.control, name: 'variant' });
    const selectedVariant = variants?.find((variant) => variant.id === selectedVariantId);

    if (!variants || variants.length <= 1) {
        return null;
    }

    return (
        <Form.TabsSelector
            asChild
            name="variant"
            defaultValue={hash}
            onValueChange={(value) => {
                if (value) {
                    switchVariant(value);
                }
            }}
        >
            <Layout
                {...props}
                data-testid="product-detail-variant-select"
                media={{
                    ...media,
                    initial: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', ...media?.initial },
                }}
            >
                {variants.map(({ id, name, priceValue, variantId }) => {
                    const diffPrice =
                        parseFloat(String(priceValue.amount)) -
                        parseFloat(String(selectedVariant?.priceValue.amount || '0'));
                    return (
                        <Form.TabsSelector.Tab
                            key={id}
                            value={id}
                            data-testid="product-detail-variant-tab"
                            data-variantitemid={variantId}
                            className="product-detail__info-variant-select-tab"
                        >
                            <span data-testid="product-detail-variant-name">{name}</span>
                            {diffPrice !== 0 && (
                                <span
                                    className="product-detail__info-variant-select-tab-price"
                                    data-testid="product-detail-variant-price-diff"
                                    data-value={diffPrice}
                                >
                                    {`${diffPrice > 0 ? '+' : ''}${getPriceValueTranslation({
                                        amount: diffPrice,
                                        currency: priceValue.currency as Currency,
                                        t,
                                    })}`}
                                </span>
                            )}
                        </Form.TabsSelector.Tab>
                    );
                })}
            </Layout>
        </Form.TabsSelector>
    );
};
